import {LegendOrdinal} from '@visx/legend';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {LabelCompatibleType} from 'ui-library/charting/Types';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import ImmutableSet from 'util/ImmutableSet';

import {
  estimateDiscreteSeriesLegendItemWidth,
  LEGEND_ITEM_HEIGHT,
  DiscreteSeriesLegendItem,
} from './DiscreteSeriesLegendItem';
import {LegendItemDirection, LegendLocation} from './LegendConstants';
import {DiscreteSeriesColorConfig, DiscreteColorScale} from './utils/getSeriesColorConfig';

export const LEGEND_LATERAL_PADDING = 30;
export const MAX_LEGEND_HEIGHT = LEGEND_ITEM_HEIGHT * 3;

export function estimateDiscreteSeriesLegendHeight(
  colorScale: DiscreteColorScale,
  chartWidth: number
): number {
  const maxLineWidth = chartWidth - LEGEND_LATERAL_PADDING * 2;
  let accumulatedWidth = 0;
  let lines = 1;
  for (const label of colorScale.domain()) {
    const stringLabel = typeof label === 'string' ? label : prettyChartNumber(label);
    const itemWidth = estimateDiscreteSeriesLegendItemWidth(stringLabel);
    if (accumulatedWidth + itemWidth > maxLineWidth) {
      lines += 1;
      accumulatedWidth = itemWidth;
    } else {
      accumulatedWidth += itemWidth;
    }
  }
  return Math.min(lines * LEGEND_ITEM_HEIGHT, MAX_LEGEND_HEIGHT);
}

export function estimateDiscreteSeriesLegendWidth(
  colorScale: DiscreteColorScale,
  maxLegendWidth: number
): number {
  let maxLabelWidth = 0;

  for (const label of colorScale.domain()) {
    const stringLabel = typeof label === 'string' ? label : prettyChartNumber(label);
    const itemWidth = estimateDiscreteSeriesLegendItemWidth(stringLabel);

    maxLabelWidth = Math.max(maxLabelWidth, itemWidth);
  }

  return Math.min(maxLegendWidth, maxLabelWidth + LEGEND_LATERAL_PADDING);
}

export interface DiscreteSeriesLegendProps {
  colorConfig: DiscreteSeriesColorConfig;
  direction?: LegendItemDirection;
  excludedSeries?: ImmutableSet<LabelCompatibleType>;
  location?: LegendLocation;
  onClickLegendItem?: (label: LabelCompatibleType) => void;
  onHoverItem?: (label?: LabelCompatibleType) => void;
  width?: number;
  height?: number;
}

export default function DiscreteSeriesLegend({
  colorConfig: {colorScale},
  direction = LegendItemDirection.ROW,
  excludedSeries,
  location = LegendLocation.TOP,
  onClickLegendItem,
  onHoverItem,
  width,
  height,
}: DiscreteSeriesLegendProps): JSX.Element {
  function maybeWrapContent(items: Array<JSX.Element>): JSX.Element | Array<JSX.Element> {
    // With flexbox justifyContent: center there is a problem scrolling all the way to the top of the
    // flex container, so instead we have to use margins on a div containing the legend items in order to
    // center them when there is no overflow, while still allowing full scrolling when ther is overflow.
    if (direction === LegendItemDirection.COLUMN) {
      return <div className={css(styles.verticalScrollFix)}>{items}</div>;
    }

    return items;
  }

  return (
    <LegendOrdinal scale={colorScale}>
      {(labels) => (
        <div
          style={{
            ...(width != null ? {width: `${width}px`} : {}),
            ...(height ? {maxHeight: `${height}px`} : {}),
          }}
          className={classNames(
            css(
              styles.legendContainer,
              direction === LegendItemDirection.ROW
                ? styles.legendHorizontalContainer
                : styles.legendVerticalContainer,
              location === LegendLocation.LEFT && styles.legendVerticalContainerLeft,
              location === LegendLocation.RIGHT && styles.legendVerticalContainerRight
            ),
            'scrollbar-override'
          )}
        >
          {maybeWrapContent(
            labels.map((label, i) => (
              <DiscreteSeriesLegendItem
                key={`legend-item-${i}`}
                label={label}
                isExcluded={excludedSeries?.has(label.datum)}
                onClickLegendItem={onClickLegendItem}
                onHover={onHoverItem}
                direction={direction}
              />
            ))
          )}
        </div>
      )}
    </LegendOrdinal>
  );
}

const styles = StyleSheet.create({
  legendContainer: {
    paddingLeft: LEGEND_LATERAL_PADDING,
    paddingRight: LEGEND_LATERAL_PADDING,
    overflowY: 'auto',
  },
  legendHorizontalContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    maxHeight: MAX_LEGEND_HEIGHT,
  },
  legendVerticalContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  legendVerticalContainerLeft: {
    paddingRight: 0,
  },
  legendVerticalContainerRight: {
    paddingLeft: 0,
  },
  verticalScrollFix: {
    marginTop: 'auto',
    marginBottom: 'auto',
  },
});
